import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Select from "react-select";
import { Html5Qrcode } from "html5-qrcode";
import { useSelector } from "react-redux";
import { selectUser } from "../../features/userSlice";
import { usePopup } from "../../utils/PopupContext";
import styles from "./LoadingEntry.module.css";
import FloatingActions from "../../components/FloatingActions";

const UpdateTestCertificate = () => {
    const user = useSelector(selectUser);
    const token = user?.token;
    const emp_id = user?.emp_id || user?.id;
    const { showPopup } = usePopup();

    const topRef = useRef(null);
    const bottomRef = useRef(null);
    const qrRef = useRef(null);
    const scannerRef = useRef(null);

    const [loadingPoints, setLoadingPoints] = useState([]);
    const [selectedPoint, setSelectedPoint] = useState(null);
    const [gps, setGps] = useState(null);

    const [qrText, setQrText] = useState("");
    const [scanning, setScanning] = useState(false);

    const [header, setHeader] = useState(null);
    const [items, setItems] = useState([]);

    const [pointMismatch, setPointMismatch] = useState(false);
    const [busy, setBusy] = useState(false);

    const [qcParams, setQcParams] = useState([]);
    const [qcLoading, setQcLoading] = useState(false);
    const [remarks, setRemarks] = useState("");
    const [disabledItems, setDisabledItems] = useState({});

    const handleToggle = (id, index) => {
        setDisabledItems((prev) => {
            const isDisabling = !prev[id];

            if (isDisabling) {
                setQcParams((prev) => {
                    const updated = [...prev];
                    updated[index] = {
                        ...updated[index],
                        value: "",
                    };
                    return updated;
                });
            }

            return {
                ...prev,
                [id]: isDisabling,
            };
        });
    };

    const authHeader = useMemo(
        () => ({
            headers: {
                Authorization: `Bearer ${token}`,
            },
        }),
        [token]
    );

    const notify = (message, type = "info") => {
        const finalMessage =
            typeof message === "string" && message.trim()
                ? message.trim()
                : "Something went wrong.";

        const variantMap = {
            success: { variant: "success", title: "Success" },
            error: { variant: "error", title: "Error" },
            warning: { variant: "warning", title: "Warning" },
            info: { variant: "info", title: "Notice" },
        };

        const cfg = variantMap[type] || variantMap.info;

        try {
            if (typeof showPopup === "function") {
                showPopup({
                    variant: cfg.variant,
                    title: cfg.title,
                    message: finalMessage,
                    autocloseMs: type === "success" ? 1500 : undefined,
                });
                return;
            }
        } catch (e) {
            console.warn("showPopup failed:", e);
        }

        alert(finalMessage);
    };

    useEffect(() => {
        fetchLoadingPoints();
        getGps();

        return () => {
            stopScanner();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const scrollToRef = (ref) => {
        setTimeout(() => {
            ref.current?.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        }, 100);
    };

    const resetScreen = () => {
        setQrText("");
        setHeader(null);
        setItems([]);
        setPointMismatch(false);
        setQcParams([]);
        setDisabledItems({});
        setRemarks("");
        scrollToRef(topRef);
    };

    const fetchLoadingPoints = async () => {
        try {
            const res = await axios.get("/api/loading/getMyLoadingPoints", authHeader);
            setLoadingPoints(res.data || []);
        } catch (err) {
            console.error(err);
            notify("Unable to load loading points.", "error");
        }
    };

    const getGps = () => {
        if (!navigator.geolocation) {
            notify("GPS is not supported on this device.", "error");
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (pos) => {
                setGps({
                    latitude: pos.coords.latitude,
                    longitude: pos.coords.longitude,
                });
            },
            () => {
                notify("Please allow GPS permission.", "error");
            },
            {
                enableHighAccuracy: true,
                timeout: 15000,
                maximumAge: 0,
            }
        );
    };

    const distanceMeter = (lat1, lon1, lat2, lon2) => {
        const R = 6371000;
        const toRad = (v) => (v * Math.PI) / 180;

        const dLat = toRad(lat2 - lat1);
        const dLon = toRad(lon2 - lon1);

        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(toRad(lat1)) *
            Math.cos(toRad(lat2)) *
            Math.sin(dLon / 2) *
            Math.sin(dLon / 2);

        return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    };

    const autoDetectPoint = () => {
        if (!gps) {
            notify("Please capture GPS first.", "error");
            return;
        }

        let nearest = null;

        loadingPoints.forEach((p) => {
            if (!p.latitude || !p.longitude) return;

            const dist = distanceMeter(
                gps.latitude,
                gps.longitude,
                Number(p.latitude),
                Number(p.longitude)
            );

            if (!nearest || dist < nearest.distance) {
                nearest = { ...p, distance: dist };
            }
        });

        if (!nearest) {
            notify("No loading point found near your GPS.", "error");
            return;
        }

        if (nearest.distance > Number(nearest.radius_meter || 100)) {
            notify("You are not inside any configured loading point area.", "error");
            return;
        }

        setSelectedPoint(nearest);
        notify(`Loading point selected: ${nearest.label}`, "success");
    };

    const parseQr = (text) => {
        const parts = String(text || "").trim().split("|");

        if (parts.length !== 3) {
            throw new Error("Invalid QR. Expected format: Entity|TCode|DocNumber");
        }

        const [p_entity_code, p_tcode, p_docnumber] = parts.map((x) => x.trim());

        if (!p_entity_code || !p_tcode || !p_docnumber) {
            throw new Error("QR data is incomplete.");
        }

        return { p_entity_code, p_tcode, p_docnumber };
    };

    const startScanner = async () => {
        try {
            if (!selectedPoint) {
                notify("Please select loading point first.", "error");
                return;
            }

            if (!window.isSecureContext) {
                notify(
                    "Camera scanning is blocked on HTTP. Please open this screen using HTTPS domain.",
                    "error"
                );
                return;
            }

            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                notify(
                    "Camera is not available in this browser. Please use mobile Chrome with HTTPS.",
                    "error"
                );
                return;
            }

            scrollToRef(qrRef);
            await stopScanner();

            const qrRegionId = "loading-qc-qr-reader";
            scannerRef.current = new Html5Qrcode(qrRegionId);

            setScanning(true);

            await scannerRef.current.start(
                { facingMode: "environment" },
                {
                    fps: 12,
                    qrbox: (viewfinderWidth, viewfinderHeight) => {
                        const minEdge = Math.min(viewfinderWidth, viewfinderHeight);
                        const qrboxSize = Math.floor(minEdge * 0.72);
                        return {
                            width: qrboxSize,
                            height: qrboxSize,
                        };
                    },
                    aspectRatio: 1.0,
                },
                async (decodedText) => {
                    await stopScanner();
                    setQrText(decodedText);
                    await fetchAdvice(decodedText);
                }
            );
        } catch (err) {
            console.error(err);
            setScanning(false);
            notify(
                err?.message ||
                "Unable to start QR scanner. Please check camera permission.",
                "error"
            );
        }
    };

    const stopScanner = async () => {
        try {
            if (!scannerRef.current) return;

            const state = scannerRef.current.getState?.();

            if (state === 2) {
                await scannerRef.current.stop();
            }

            await scannerRef.current.clear();
            scannerRef.current = null;
        } catch (err) {
            console.warn("Scanner cleanup ignored:", err?.message || err);
            scannerRef.current = null;
        } finally {
            setScanning(false);
        }
    };

    const fetchAdvice = async (text = qrText) => {
        try {
            if (!selectedPoint) {
                notify("Please select loading point first.", "error");
                return;
            }

            const payload = parseQr(text);
            setBusy(true);

            const res = await axios.post(
                "/api/loading/getAdviceDetails",
                payload,
                authHeader
            );

            const adviceHeader = res.data?.header;
            const adviceItems = res.data?.items || [];

            if (!adviceHeader) {
                notify("Loading advice not found.", "error");
                setHeader(null);
                setItems([]);
                setQcParams([]);
                return;
            }

            const isMismatch =
                selectedPoint?.value &&
                adviceHeader?.loading_point_code &&
                String(selectedPoint.value).trim().toUpperCase() !==
                String(adviceHeader.loading_point_code).trim().toUpperCase();

            setHeader(adviceHeader);
            setItems(adviceItems);
            setPointMismatch(Boolean(isMismatch));
            setRemarks(adviceHeader.qc_remarks || "");

            if (isMismatch) {
                notify(
                    `Wrong loading point. Selected: ${selectedPoint?.label || selectedPoint?.value
                    }, Advice: ${adviceHeader.loading_point_name || adviceHeader.loading_point_code
                    }.`,
                    "error"
                );
            } else {
                await fetchQcParams(adviceHeader, adviceItems);
            }

            setTimeout(() => {
                document.getElementById("loading-qc-details-card")?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                });
            }, 200);
        } catch (err) {
            console.error(err);
            notify(
                err.response?.data?.message || err.message || "Unable to fetch advice.",
                "error"
            );
        } finally {
            setBusy(false);
        }
    };

    const fetchQcParams = async (adviceHeader, adviceItems) => {
        try {
            setQcLoading(true);

            const res = await axios.post(
                "/api/loading/getQcParamsForAdvice",
                {
                    entity_code: adviceHeader.p_entity_code,
                    docnumber: adviceHeader.p_docnumber,
                },
                authHeader
            );

            const data = res.data || [];

            const formatted = data.map((p) => {
                if (p.remarks && !remarks) setRemarks(p.remarks);

                return {
                    slno: p.slno,
                    item_code: p.item_code,
                    item_desc: p.item_desc,
                    test_code: p.test_code,
                    test_desc: p.test_desc,
                    min_value: p.min_value,
                    max_value: p.max_value,
                    value: p.test_value || "",
                };
            });

            setQcParams(formatted);

            // Add test_code to disabledItems when value is empty
            const disabled = {};

            formatted.forEach((item) => {
                if (item.value === "") {
                    disabled[item.test_code] = true;
                }
            });

            setDisabledItems(disabled);
        } catch (err) {
            // console.error("QC param load error:", err);
            setQcParams([]);
            notify(err.response?.data?.message || "Unable to load QC parameters.", "error");
        } finally {
            setQcLoading(false);
        }
    };

    const handleQcChange = (index, value) => {
        if (!/^\d*\.?\d*$/.test(value)) return;

        setQcParams((prev) => {
            const updated = [...prev];
            updated[index] = { ...updated[index], value };
            return updated;
        });
    };

    const handleUpdateQc = async () => {
        try {
            if (!header) {
                notify("Please scan Loading Advice first.", "error");
                return;
            }

            if (pointMismatch) {
                notify("Wrong loading point. QC cannot be updated.", "error");
                return;
            }

            const qcDetails = qcParams.map((p) => ({
                slno: p.slno,
                item_code: p.item_code,
                test_code: p.test_code,
                test_value: String(p.value ?? "").trim(),
            }));

            if (qcDetails.length === 0) {
                notify("No QC parameters found to update.", "warning");
                return;
            }

            const ok = window.confirm(
                `Update QC parameters for Advice: ${header.p_docnumber}?`
            );

            if (!ok) return;

            setBusy(true);

            const payload = {
                p_entity_code: header.p_entity_code,
                p_tcode: header.p_tcode,
                p_docnumber: header.p_docnumber,
                qc_details: qcDetails,
                remarks: remarks || "",
                loading_point_code: header.loading_point_code,
                loading_point_name: header.loading_point_name,
                vehicle_no: header.vehicle_no,
                qc_required: "Y",
                emp_id,
            };

            const res = await axios.post(
                "/api/loading/updateLoadingAdviceQC",
                payload,
                authHeader
            );

            notify(res.data?.message || "QC parameters updated successfully.", "success");
            resetScreen();
        } catch (err) {
            // console.error("Update QC error:", err);
            notify(err.response?.data?.message || "QC update failed.", "error");
        } finally {
            setBusy(false);
        }
    };

    const existingStartTime =
        header?.p_loading_start || header?.P_LOADING_START || "";

    const existingEndTime = header?.p_loading_end || header?.P_LOADING_END || "";

    if (!token) {
        return <div>Please login first.</div>;
    }

    return (
        <div className={styles.page} ref={topRef}>
            <div className={styles.header}>
                <div>
                    <div className={styles.title}>Loading Advice QC Update</div>
                    <div className={styles.subTitle}>Scan loading advice and update QC parameters for certificate printing</div>
                </div>

                <div className={header ? styles.activeBadge : styles.pendingBadge}>
                    {header ? "Advice Loaded" : "Pending"}
                </div>
            </div>

            <div className={styles.card}>
                <div className={styles.sectionTitle}>1. Loading Point</div>

                <div className={styles.gpsRow}>
                    <div className={styles.gpsText}>
                        {gps
                            ? `GPS: ${gps.latitude.toFixed(6)}, ${gps.longitude.toFixed(6)}`
                            : "GPS not captured"}
                    </div>

                    <button className={styles.smallBtn} onClick={getGps} disabled={busy}>
                        GPS
                    </button>
                </div>

                <Select
                    value={selectedPoint}
                    options={loadingPoints}
                    onChange={setSelectedPoint}
                    placeholder="Select Loading Point"
                    classNamePrefix="react-select"
                />

                <button
                    className={styles.secondaryBtn}
                    onClick={autoDetectPoint}
                    disabled={busy}
                >
                    Auto Detect by GPS
                </button>
            </div>

            <div className={styles.card}>
                <div className={styles.sectionTitle}>2. Scan Loading Advice QR</div>

                {!window.isSecureContext && (
                    <div className={styles.warningBox}>
                        Camera QR scanning needs HTTPS. Please open this screen using HTTPS
                        domain for mobile camera scanning.
                    </div>
                )}

                <div className={styles.qrShell}>
                    <div
                        id="loading-qc-qr-reader"
                        ref={qrRef}
                        className={styles.qrBox}
                    ></div>

                    {scanning && (
                        <div className={styles.qrHint}>
                            Align QR code inside the square box
                        </div>
                    )}
                </div>

                <div className={styles.btnRow}>
                    {!scanning ? (
                        <button
                            className={styles.primaryBtn}
                            onClick={startScanner}
                            disabled={busy || !selectedPoint}
                        >
                            Start QR Scan
                        </button>
                    ) : (
                        <button className={styles.dangerBtn} onClick={stopScanner}>
                            Stop Scan
                        </button>
                    )}
                </div>

                <input
                    className={styles.input}
                    value={qrText}
                    onChange={(e) => setQrText(e.target.value)}
                    placeholder="Or paste QR text: Entity|TCode|DocNo"
                />

                <button
                    className={styles.secondaryBtn}
                    onClick={() => fetchAdvice()}
                    disabled={busy || !selectedPoint}
                >
                    Fetch Advice
                </button>
            </div>

            {header && (
                <div className={styles.card} id="loading-qc-details-card">
                    <div className={styles.sectionTitle}>3. Advice Details</div>

                    {pointMismatch && (
                        <div className={styles.warningBox}>
                            Wrong Loading Point. Selected point and advice point are different.
                        </div>
                    )}

                    <div className={styles.infoGrid}>
                        <div>
                            <span>Doc No</span>
                            <b>{header.p_docnumber}</b>
                        </div>

                        <div>
                            <span>Vehicle</span>
                            <b>{header.vehicle_no || "-"}</b>
                        </div>

                        <div>
                            <span>Advice Point</span>
                            <b>
                                {header.loading_point_name || header.loading_point_code || "-"}
                            </b>
                        </div>

                        <div>
                            <span>TCode</span>
                            <b>{header.p_tcode}</b>
                        </div>

                        <div>
                            <span>Start Time</span>
                            <b>{existingStartTime || "-"}</b>
                        </div>

                        <div>
                            <span>End Time</span>
                            <b>{existingEndTime || "-"}</b>
                        </div>
                    </div>

                    <div className={styles.itemList}>
                        {items.map((r) => (
                            <div key={`item-${r.slno}`} className={styles.itemCard}>
                                <div className={styles.itemHead}>
                                    <div className={styles.itemCode}>{r.item_code}</div>
                                    <div className={styles.itemReqQty}>
                                        Required: <b>{r.qty_required ?? "-"}</b>
                                    </div>
                                </div>

                                <div className={styles.itemDesc}>{r.item_desc}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {header && (
                <div className={styles.card}>
                    <div className={styles.sectionTitle}>4. QC Parameters</div>

                    <label className={styles.label}>QC Remarks</label>
                    <textarea
                        className={styles.textarea}
                        value={remarks}
                        onChange={(e) => setRemarks(e.target.value)}
                        placeholder="Enter QC remarks"
                    />

                    {qcLoading && (
                        <div className={styles.warningBox}>
                            Fetching QC parameters...
                        </div>
                    )}

                    {!qcLoading && qcParams.length === 0 && (
                        <div className={styles.warningBox}>
                            No QC parameters configured for this material.
                        </div>
                    )}

                    {!qcLoading &&
                        qcParams.map((param, index) => (
                            <div key={`${param.item_code}-${param.test_code}-${index}`} className={styles.itemCard} style={{ backgroundColor: disabledItems[param.test_code] ? "#ebebeb" : "" }}>
                                <div className={styles.itemHead}>
                                    <div className={styles.itemCode}>
                                        {param.test_code} - {param.test_desc}
                                    </div>

                                    <button onClick={() => handleToggle(param.test_code, index)}

                                        className={
                                            disabledItems[param.test_code] ? styles.btnEnable : styles.btnDelete}
                                    >
                                        {disabledItems[param.test_code] ? "Enable" : "Delete"}
                                    </button>
                                </div>

                                <div className={styles.itemDesc}>
                                    Material: <b>{param.item_code}</b>
                                    {param.item_desc ? ` - ${param.item_desc}` : ""}
                                </div>

                                {(param.min_value || param.max_value) && (
                                    <div className={styles.itemDesc}>
                                        Limit: {param.min_value || "-"} to {param.max_value || "-"}
                                    </div>
                                )}

                                <div className={styles.itemQtyRow}>
                                    <label className={styles.itemQtyLabel}>Observed Value</label>
                                    <input
                                        type="text"
                                        inputMode="decimal"
                                        className={styles.itemQtyInput}
                                        placeholder="Enter observed value"
                                        value={param.value}
                                        onChange={(e) => handleQcChange(index, e.target.value)}
                                        disabled={disabledItems[param.test_code]}
                                    />
                                </div>
                            </div>
                        ))}

                    <button
                        className={styles.successBtn}
                        onClick={handleUpdateQc}
                        disabled={busy || pointMismatch}
                    >
                        Update QC Parameters
                    </button>
                </div>
            )}

            <FloatingActions topRef={topRef} bottomRef={bottomRef} />
            <div ref={bottomRef} />
        </div>
    );
};

export default UpdateTestCertificate;